import { logAdminAction } from "./auditLog";
import { supabase } from "./supabase";

export type PrizeStructureRow = { place: number; amount_cents: number; label?: string };

export type TeamSplitPolicy = "equal" | "captain_takes_all";

export type WinnerSelectionStatus = "pending_admin_review" | "approved" | "rejected" | "superseded";

export type WinnerSelection = {
  id: string;
  competition_id: string;
  source: "leaderboard_auto" | "admin_override";
  generated_at: string;
  notes: string | null;
  status: WinnerSelectionStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
};

export type WinnerSelectionTeam = {
  selection_id: string;
  team_id: string;
  place: number;
  amount_cents: number;
  weighted_score: number | null;
};

export type PrizeDisbursementStatus =
  | "pending_tax_info"
  | "ready_to_pay"
  | "paid"
  | "forfeited";

export type PrizeDisbursement = {
  id: string;
  selection_id: string;
  competition_id: string;
  student_id: string;
  team_id: string;
  place: number;
  amount_cents: number;
  status: PrizeDisbursementStatus;
  tax_info_required: boolean;
  tax_info_status: "not_started" | "requested" | "received" | "not_required";
  payment_rail: string | null;
  payment_reference: string | null;
  paid_at: string | null;
  forfeited_at: string | null;
  forfeit_reason: string | null;
  created_at: string;
};

// ─── Setup helpers (client/admin edit page) ────────────────────────

/**
 * Saves winner config to a competition row. The columns live on
 * `competitions` directly so this is a single update — no separate
 * table to keep in sync. The DB CHECK constraints validate the count
 * range and the team_split_policy enum; the UI validates the
 * prize_structure shape (one row per place, amounts numeric > 0).
 */
export async function saveCompetitionWinnerConfig(
  competitionId: string,
  patch: {
    winner_count?: number;
    prize_structure?: PrizeStructureRow[];
    team_split_policy?: TeamSplitPolicy;
  }
): Promise<{ error: Error | null }> {
  const { error } = await supabase
    .from("competitions")
    .update({
      ...(patch.winner_count !== undefined ? { winner_count: patch.winner_count } : {}),
      ...(patch.prize_structure !== undefined ? { prize_structure: patch.prize_structure } : {}),
      ...(patch.team_split_policy !== undefined ? { team_split_policy: patch.team_split_policy } : {}),
      updated_at: new Date().toISOString(),
    })
    .eq("id", competitionId);
  return { error: error ? new Error(error.message) : null };
}

export async function getCompetitionWinnerConfig(
  competitionId: string
): Promise<{
  winner_count: number;
  prize_structure: PrizeStructureRow[];
  team_split_policy: TeamSplitPolicy;
} | null> {
  const { data, error } = await supabase
    .from("competitions")
    .select("winner_count, prize_structure, team_split_policy")
    .eq("id", competitionId)
    .maybeSingle();
  if (error || !data) return null;
  return {
    winner_count: data.winner_count as number,
    prize_structure: (data.prize_structure as PrizeStructureRow[]) ?? [],
    team_split_policy: data.team_split_policy as TeamSplitPolicy,
  };
}

/** Returns true once any approved selection exists for the competition.
 * Locks the winners-and-prizes setup form in that state. */
export async function competitionHasApprovedSelection(
  competitionId: string
): Promise<boolean> {
  const { count } = await supabase
    .from("winner_selections")
    .select("id", { count: "exact", head: true })
    .eq("competition_id", competitionId)
    .eq("status", "approved");
  return (count ?? 0) > 0;
}

// ─── Admin review surface ──────────────────────────────────────────

export type SelectionDetail = {
  selection: WinnerSelection;
  teams: Array<
    WinnerSelectionTeam & {
      team_name: string;
      members: Array<{ student_id: string; full_name: string | null; email: string | null }>;
    }
  >;
};

/**
 * Hydrate the "review winners" page in one read: pulls the latest
 * non-superseded selection, its line items, the team names, and the
 * member roster per team. Resolves the standard PostgREST one-vs-many
 * relation shape gotcha by always treating embeds as arrays.
 */
export async function getLatestPendingSelection(
  competitionId: string
): Promise<SelectionDetail | null> {
  const { data: rows } = await supabase
    .from("winner_selections")
    .select("*")
    .eq("competition_id", competitionId)
    .in("status", ["pending_admin_review", "approved", "rejected"])
    .order("generated_at", { ascending: false })
    .limit(1);
  const sel = (rows ?? [])[0] as WinnerSelection | undefined;
  if (!sel) return null;

  const { data: teamRows } = await supabase
    .from("winner_selection_teams")
    .select("*, team:teams(name)")
    .eq("selection_id", sel.id)
    .order("place", { ascending: true });

  type TeamRow = WinnerSelectionTeam & { team: { name: string } | { name: string }[] | null };
  const teams = (teamRows ?? []) as TeamRow[];
  if (teams.length === 0) return { selection: sel, teams: [] };

  // Hydrate roster + names in one batch.
  const teamIds = teams.map((t) => t.team_id);
  const { data: memberRows } = await supabase
    .from("team_members")
    .select("team_id, student_id")
    .in("team_id", teamIds);

  type MemberRow = { team_id: string; student_id: string };
  const members = (memberRows ?? []) as MemberRow[];
  const studentIds = Array.from(new Set(members.map((m) => m.student_id)));
  let profileMap = new Map<string, { full_name: string | null; email: string | null }>();
  if (studentIds.length > 0) {
    const { data: profileRows } = await supabase
      .from("user_profiles")
      .select("id, full_name, email")
      .in("id", studentIds);
    type ProfileRow = { id: string; full_name: string | null; email: string | null };
    profileMap = new Map(
      ((profileRows ?? []) as ProfileRow[]).map((p) => [p.id, { full_name: p.full_name, email: p.email }])
    );
  }

  const membersByTeam = new Map<string, Array<{ student_id: string; full_name: string | null; email: string | null }>>();
  for (const m of members) {
    const arr = membersByTeam.get(m.team_id) ?? [];
    const profile = profileMap.get(m.student_id) ?? { full_name: null, email: null };
    arr.push({ student_id: m.student_id, full_name: profile.full_name, email: profile.email });
    membersByTeam.set(m.team_id, arr);
  }

  return {
    selection: sel,
    teams: teams.map((t) => {
      const team = Array.isArray(t.team) ? t.team[0] : t.team;
      return {
        selection_id: t.selection_id,
        team_id: t.team_id,
        place: t.place,
        amount_cents: t.amount_cents,
        weighted_score: t.weighted_score,
        team_name: team?.name ?? "Team",
        members: membersByTeam.get(t.team_id) ?? [],
      };
    }),
  };
}

export async function regenerateWinnerSelection(
  competitionId: string
): Promise<{ id: string | null; error: Error | null }> {
  const { data, error } = await supabase.rpc("generate_winner_selection_from_leaderboard", {
    p_competition_id: competitionId,
  });
  if (error) return { id: null, error: new Error(error.message) };
  return { id: typeof data === "string" ? data : null, error: null };
}

export type OverrideWinnersInput = {
  competitionId: string;
  winners: Array<{ team_id: string; place: number }>;
  notes: string;
};

export async function adminOverrideWinnerSelection(
  input: OverrideWinnersInput
): Promise<{ id: string | null; error: Error | null }> {
  const { data, error } = await supabase.rpc("admin_override_winner_selection", {
    p_competition_id: input.competitionId,
    p_winners: input.winners,
    p_notes: input.notes,
  });
  if (error) return { id: null, error: new Error(error.message) };
  const newId = typeof data === "string" ? data : null;
  await logAdminAction({
    action: "winner_selection.override",
    entityType: "winner_selections",
    entityId: newId,
    summary: `Override winner selection (${input.winners.length} winners): ${input.notes.slice(0, 80)}`,
    diff: { after: { winners: input.winners, notes: input.notes } },
  });
  return { id: newId, error: null };
}

export async function approveWinnerSelection(
  selectionId: string,
  reviewNotes?: string | null
): Promise<{ count: number; error: Error | null }> {
  const { data, error } = await supabase.rpc("approve_winner_selection", {
    p_selection_id: selectionId,
    p_review_notes: reviewNotes ?? null,
  });
  if (error) return { count: 0, error: new Error(error.message) };
  const count = typeof data === "number" ? data : 0;
  void import("./analytics").then(({ track }) =>
    track("winner_selection_approved", {
      selection_id: selectionId,
      disbursement_count: count,
    })
  );
  await logAdminAction({
    action: "winner_selection.approve",
    entityType: "winner_selections",
    entityId: selectionId,
    summary: `Approved winner selection — ${count} disbursement(s) created`,
    diff: { after: { status: "approved", review_notes: reviewNotes ?? null, disbursement_count: count } },
  });
  return { count, error: null };
}

export async function rejectWinnerSelection(
  selectionId: string,
  reviewNotes: string
): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc("reject_winner_selection", {
    p_selection_id: selectionId,
    p_review_notes: reviewNotes,
  });
  if (error) return { error: new Error(error.message) };
  await logAdminAction({
    action: "winner_selection.reject",
    entityType: "winner_selections",
    entityId: selectionId,
    summary: `Rejected winner selection: ${reviewNotes.slice(0, 80)}`,
    diff: { after: { status: "rejected", review_notes: reviewNotes } },
  });
  return { error: null };
}

// ─── Disbursement admin actions ────────────────────────────────────

export type AdminDisbursementRow = PrizeDisbursement & {
  student_name: string | null;
  student_email: string | null;
  competition_name: string;
};

export async function listAllDisbursements(): Promise<AdminDisbursementRow[]> {
  // Two-step hydrate to dodge PostgREST embed ambiguity on
  // user_profiles (multiple FKs from prize_disbursements vector through
  // selection / disbursements aren't disambiguated automatically).
  const { data: rows } = await supabase
    .from("prize_disbursements")
    .select("*, competition:competitions(name)")
    .order("created_at", { ascending: false });

  type Row = PrizeDisbursement & {
    competition: { name: string } | { name: string }[] | null;
  };
  const list = (rows ?? []) as Row[];
  if (list.length === 0) return [];

  const studentIds = Array.from(new Set(list.map((r) => r.student_id)));
  const { data: profileRows } = await supabase
    .from("user_profiles")
    .select("id, full_name, email")
    .in("id", studentIds);
  type Profile = { id: string; full_name: string | null; email: string | null };
  const byId = new Map(
    ((profileRows ?? []) as Profile[]).map((p) => [p.id, p] as const)
  );

  return list.map((r) => {
    const c = Array.isArray(r.competition) ? r.competition[0] : r.competition;
    const p = byId.get(r.student_id);
    return {
      ...r,
      student_name: p?.full_name ?? null,
      student_email: p?.email ?? null,
      competition_name: c?.name ?? "Competition",
    };
  });
}

export async function markDisbursementPaid(
  id: string,
  rail: "manual" | "qbo" | "venmo" | "zelle" | "ach" | "check",
  reference?: string | null
): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc("mark_disbursement_paid", {
    p_disbursement_id: id,
    p_payment_rail: rail,
    p_payment_reference: reference ?? null,
  });
  if (error) return { error: new Error(error.message) };
  await logAdminAction({
    action: "disbursement.paid",
    entityType: "prize_disbursements",
    entityId: id,
    summary: `Marked disbursement paid via ${rail}${reference ? ` (ref: ${reference})` : ""}`,
    diff: { after: { status: "paid", payment_rail: rail, payment_reference: reference ?? null } },
  });
  return { error: null };
}

export async function markDisbursementForfeited(
  id: string,
  reason: string
): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc("mark_disbursement_forfeited", {
    p_disbursement_id: id,
    p_reason: reason,
  });
  if (error) return { error: new Error(error.message) };
  await logAdminAction({
    action: "disbursement.forfeit",
    entityType: "prize_disbursements",
    entityId: id,
    summary: `Marked disbursement forfeited: ${reason.slice(0, 80)}`,
    diff: { after: { status: "forfeited", forfeit_reason: reason } },
  });
  return { error: null };
}

// ─── Student winnings ──────────────────────────────────────────────

export type StudentDisbursementRow = PrizeDisbursement & {
  competition: { id: string; name: string };
};

export async function listMyDisbursements(): Promise<StudentDisbursementRow[]> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id ?? null;
  if (!uid) return [];

  const { data: rows } = await supabase
    .from("prize_disbursements")
    .select("*, competition:competitions(id, name)")
    .eq("student_id", uid)
    .order("created_at", { ascending: false });

  type Row = PrizeDisbursement & {
    competition: { id: string; name: string } | { id: string; name: string }[] | null;
  };
  return ((rows ?? []) as Row[]).map((r) => {
    const c = Array.isArray(r.competition) ? r.competition[0] : r.competition;
    return {
      ...r,
      competition: c ?? { id: r.competition_id, name: "Competition" },
    };
  });
}

// ─── Helpers ───────────────────────────────────────────────────────

export function formatUsd(cents: number): string {
  return "$" + (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatUsdCompact(cents: number): string {
  return "$" + (cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export function placeLabel(place: number, custom?: string): string {
  if (custom?.trim()) return custom;
  const n = place;
  const suffix = n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th";
  return n + suffix + " place";
}
